<template>
    <analytics-sidebar-box :canReset="isChanged" @reset="modelProxy = [...defaultValues]">
        <template v-slot:title>
            <span :id="labelId"><slot name="title" /></span>
        </template>
        <div role="group" :aria-labelledby="labelId" class="flex flex-col gap-3" data-test="checkbox-group">
            <div
                v-for="option in props.options"
                :key="option.value"
                class="flex gap-2 items-center text-xs text-slate-800 cursor-pointer"
                :class="{
                    'opacity-70 cursor-not-allowed!': isDisabled(option.value),
                }"
                :data-test="`checkboxes-${option.value}`"
                @click.prevent="toggleValue(option.value)"
                @keydown.space.prevent="toggleValue(option.value)"
                @keydown.enter.prevent="toggleValue(option.value)"
            >
                <vx-checkbox
                    :modelValue="modelProxy.includes(option.value)"
                    :aria-label="option.label"
                    :aria-disabled="isDisabled(option.value) || undefined"
                ></vx-checkbox>
                {{ option.label }}
            </div>
        </div>
    </analytics-sidebar-box>
</template>

<script lang="ts" setup>
import { VxCheckbox } from '@voxie/frontend-components';
import AnalyticsSidebarBox from './AnalyticsSidebarBox.vue';
import { computed, useId, type PropType } from 'vue';

const props = defineProps({
    options: {
        required: true,
        type: Array as PropType<Readonly<{ label: string; value: string | number }[]>>,
    },
    disabled: Boolean,
});

const isDisabled = (value: string | number) => {
    return (modelProxy.value.length === 1 && modelProxy.value.includes(value)) || props.disabled;
};

const defaultValues = computed(() => {
    return props.options.map((option) => option.value);
});

const model = defineModel({ required: false, type: Array as PropType<(string | number)[]> });

const labelId = useId();

const modelProxy = computed({
    set: (newValues: (string | number)[]) => {
        model.value = newValues.length ? newValues : defaultValues.value;
    },
    get: () => model.value || defaultValues.value,
});

const isChanged = computed(() => {
    return (
        modelProxy.value.length !== defaultValues.value.length ||
        modelProxy.value.some((value) => !defaultValues.value.includes(value))
    );
});

const toggleValue = (optionValue: string | number) => {
    if (isDisabled(optionValue)) {
        return;
    }

    if (modelProxy.value.includes(optionValue)) {
        if (modelProxy.value.length === 1) {
            return;
        }
        modelProxy.value = modelProxy.value.filter((type) => type !== optionValue);
    } else {
        modelProxy.value = [...modelProxy.value, optionValue];
    }
};
</script>
